import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { terminalAllows, type AuthSession, type AuthUser, type TerminalInfo } from '@erp/shared';
import { api, setCsrfToken } from './api';

export interface SessionData extends AuthSession {
  serverTime: string;
  settings: Record<string, unknown> & { companyName?: string; currency?: string; locale?: string };
}

interface AuthContextValue {
  session: SessionData | null;
  loading: boolean;
  login: (input: { identifier: string; password: string; terminalCode?: string }) => Promise<void>;
  loginBadge: (input: { badgeCode: string; terminalCode?: string }) => Promise<void>;
  logout: () => Promise<void>;
  can: (permission: string) => boolean;
  refresh: () => Promise<void>;
  user: AuthUser | null;
  terminal: TerminalInfo | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [bootstrapped, setBootstrapped] = useState(false);

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api<SessionData>('/auth/me'),
    retry: false,
    staleTime: 5 * 60_000
  });

  useEffect(() => {
    if (me.data) setCsrfToken(me.data.csrfToken);
    if (me.isSuccess || me.isError) setBootstrapped(true);
  }, [me.data, me.isSuccess, me.isError]);

  useEffect(() => {
    const onUnauthorized = () => {
      setCsrfToken(null);
      qc.setQueryData(['me'], null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
    };
    window.addEventListener('erp:unauthorized', onUnauthorized);
    return () => window.removeEventListener('erp:unauthorized', onUnauthorized);
  }, [qc]);

  const login = useCallback(async (input: { identifier: string; password: string; terminalCode?: string }) => {
    const result = await api<SessionData>('/auth/login', { method: 'POST', body: input });
    setCsrfToken(result.csrfToken);
    await qc.invalidateQueries({ queryKey: ['me'] });
    await qc.fetchQuery({ queryKey: ['me'], queryFn: () => api<SessionData>('/auth/me') });
  }, [qc]);

  const loginBadge = useCallback(async (input: { badgeCode: string; terminalCode?: string }) => {
    const result = await api<SessionData>('/auth/badge', { method: 'POST', body: input });
    setCsrfToken(result.csrfToken);
    await qc.invalidateQueries({ queryKey: ['me'] });
    await qc.fetchQuery({ queryKey: ['me'], queryFn: () => api<SessionData>('/auth/me') });
  }, [qc]);

  const logout = useCallback(async () => {
    try { await api('/auth/logout', { method: 'POST', body: {} }); } catch { /* sesión ya cerrada */ }
    setCsrfToken(null);
    qc.setQueryData(['me'], null);
    // Recarga completa: limpia cualquier consulta en vuelo y vuelve al login
    window.location.replace('/');
  }, [qc]);

  const can = useCallback((permission: string) => {
    const permissions = me.data?.permissions ?? [];
    if (!(permissions.includes('admin.all') || permissions.includes(permission))) return false;
    return terminalAllows(me.data?.terminal?.kind ?? null, permission.split('.')[0] ?? '');
  }, [me.data]);

  const value = useMemo<AuthContextValue>(() => ({
    session: me.data ?? null,
    loading: !bootstrapped,
    login,
    loginBadge,
    logout,
    can,
    refresh: async () => { await qc.invalidateQueries({ queryKey: ['me'] }); },
    user: me.data?.user ?? null,
    terminal: me.data?.terminal ?? null
  }), [me.data, bootstrapped, login, loginBadge, logout, can, qc]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return context;
}
